import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ComponentFactoryResolver, Injectable, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BissSignedContent, DownloadLinkData, IBissLoginResponse } from '@app/activation/activation.model';
import { BissError, BissFailCallbackType, EventType, IBIssDialogData, IBissPorts, IBissResponseGetSigner, IBissSignedResponse, ISpringResponseBody, SignedContent } from '@app/activation/services/ibiss';
import { AppConfig } from '@app/config/app.config';
import { BissDialogComponent } from '@app/core/auth/component/biss-dialog/biss-dialog.component';
import { BehaviorSubject, catchError, delay, empty, EMPTY, flatMap, forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { observeTypes } from '../interfaces/response-interfaces';
import { OpenBissComponent } from '@app/core/auth/component/open-biss/open-biss.component';
import { HeadersSetterHelper } from '../helpers/http-headers-setter-helper';
import { HandleError } from './http-error-handler.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { InfoService } from '@app/core/info/info.service';
import { ActivationService } from '@app/activation/services/activation.service';
import { AngularDeviceInformationService } from 'angular-device-information';


@Injectable({
  providedIn: 'root'
})
export class BissService {
  private downloadDocsCallback: () => Observable<SignedContent[]>;
  private bissDialogRef: MatDialogRef<BissDialogComponent>;
  usedPort: any;
  usedCert: any;
  oSystem: string;

  private handleError: HandleError;
  private headerHelper: HeadersSetterHelper;

  private rootViewContainer: ViewContainerRef | null = null;
  private usedCertSubject = new BehaviorSubject<string | null>(null);
  usedCert$ = this.usedCertSubject.asObservable();


  operatingSystem: string;
  downloadInfo: DownloadLinkData;

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private factoryResolver: ComponentFactoryResolver,
    private router: Router,
    private translateService: TranslateService,
    public info: InfoService,

    private deviceInfoService: AngularDeviceInformationService,
    private activationService: ActivationService,


  ) { }
  public setRootViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.rootViewContainer = viewContainerRef;
  }
  getBissPort(failCallbackType: BissFailCallbackType = BissFailCallbackType.RETRY): Observable<number> {
    return this.checkBiss().pipe(
      switchMap((port) => {
        console.log('failCallbackType', failCallbackType)

        if (port) {
          this.usedPort = port;
          return of(port);
        } else {
          if (failCallbackType !== 0) {
            console.log('failCallbackType', failCallbackType)
            this.bissDialogRef.componentInstance.changeBissDownloadText();
            this.bissDialogRef = this.dialog.open(BissDialogComponent);
          }
          if (failCallbackType === BissFailCallbackType.RETRY) {
            if (!this.rootViewContainer) {
              console.error('Root ViewContainerRef is not set!');
              return throwError(() => new Error('Root ViewContainerRef is not set!'));
            }
            const factory = this.factoryResolver.resolveComponentFactory(OpenBissComponent);
            const component = factory.create(this.rootViewContainer.injector);
            this.rootViewContainer.insert(component.hostView);
            return of(null).pipe(
              delay(10000),
              switchMap(() => this.getBissPort(BissFailCallbackType.DOWNLOAD))
            );
          } else if (failCallbackType === BissFailCallbackType.DOWNLOAD) {
            this.bissDialogRef.componentInstance.changeBissDownloadText();
            return EMPTY;
          } else {
            this.logEvent(EventType.FIND_BISS_ACTIVE_PORT, true).subscribe();
            return EMPTY;
          }

        }
      })
    );
  }
  private getDocs(): Observable<SignedContent[]> {
    const url = AppConfig.endpoints.bissLogin;

    return this.http.get<IBissSignedResponse>(url).pipe(
      catchError((httperror) => {
        return of(httperror);
      }),
      switchMap((response: IBissSignedResponse) => {
        if (response.code === 'OK') {
          const signedContents: SignedContent[] = [
            new SignedContent(
              response.data.content,
              response.data.signedContent,
              response.data.signedContentCert,
              response.data.time,
              response.data.originalContent
            )
          ];
          return of(signedContents);
        } else {
          return EMPTY;
        }
      })
    );
  }
  openBissDialog(): Observable<any> {
    this.getDownloadInfoLink()
    const bissDialogData: IBIssDialogData = {
      verificationMessage: this.translateService.instant('activation.biss.checkForBiss'),
      operationMessage: this.translateService.instant('activation.biss.waitSignWithBiss')
    };
    return this.setDocsDownloadCallback(this.getDocs)
      .openBiss(bissDialogData)
      .pipe(
        switchMap((bissResult: any[]) => this.validate(bissResult)),
        catchError(e => this.handleBissError(e))
      );
  }
  setDocsDownloadCallback(callback: () => Observable<SignedContent[]>) {
    this.downloadDocsCallback = callback;
    return this;

  }


  openBiss(dialogData: IBIssDialogData, disableAutoClose: boolean = false, showValidCerts: boolean = true) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = dialogData;
    this.bissDialogRef = this.dialog.open(BissDialogComponent, dialogConfig);
    return this.getBissPort().pipe(
      switchMap(() => this.getBissCert(showValidCerts)),
      switchMap(() => this.downloadDocsCallback()),
      switchMap((signingResults: SignedContent[]) => this.bissSign(signingResults)),
      catchError((e) => {
        throw new BissError('biss.generalError');
      })
    );
  }
  getDownloadInfoLink() {
    this.operatingSystem = this.deviceInfoService.isDesktop() ? this.deviceInfoService.getDeviceInfo().os : 'WINDOWS';
    if (this.operatingSystem) {
      this.activationService.getDownloadInfo(this.mapOperatingSystem(this.operatingSystem)).subscribe((res: any) => {
        if (res.status === 200) {
          this.downloadInfo = res.data;
        }
      })
    } else {
      this.info.error(this.translateService.instant('activation.biss.osNotSupported'));
    }
  }
  mapOperatingSystem(os: string) {
    if (os.toLowerCase().includes('mac')) {
      return 'MAC';
    }
    return os;
  }

  setUsedCert(cert: string): void {
    this.usedCertSubject.next(cert);
  }
  private getBissCert(showValidCertsParam: boolean): Observable<string> {
    this.bissDialogRef.componentInstance.changeText();
    const getSignerUrl = `https://localhost:${this.usedPort}/getsigner`;
    const body = {
      selector: {},
      showValidCerts: showValidCertsParam
    };
    return this.http.post<IBissResponseGetSigner>(getSignerUrl, body).pipe(
      switchMap((response: IBissResponseGetSigner) => {
        if (response.reasonCode === 200) {
          this.usedCert = response.chain[0];
          this.setUsedCert(response.chain[0]);
          this.logEvent(EventType.FIND_BISS_CERT, false).subscribe();
          return of(response.chain[0]);
        } else {
          this.logEvent(EventType.FIND_BISS_CERT, true).subscribe();
          return throwError(() => new Error());
        }
      }),
      catchError(e => this.handleBissError(e))
    );
  }

  bissLogin() {
    const url = AppConfig.endpoints.bissLogin;
    this.http.get(`${url}`).pipe(
      catchError((error) => {
        console.error('HTTP Error:', error);
        this.info.error(this.translateService.instant(error.error.message
        ));
        return throwError(() => error);
      })
    ).subscribe((res: any) => {
      if (res.status === 200) {
        const dialogRef = this.dialog.open(OpenBissComponent, { data: res });

        dialogRef.afterClosed().subscribe(result => { });

        return of(null).pipe(
          delay(10000),
          switchMap(() => this.getBissPort(BissFailCallbackType.DOWNLOAD))
        );
      } else {
        this.info.error(this.translateService.instant('info.error'));
        return throwError(() => new Error('Login failed'));
      }
    });
  }

  logEvent(event: EventType, isFailed: boolean, portalReqId?: number): Observable<ISpringResponseBody | any> {
    const requestBody = {
      eventType: event,
      failed: isFailed,
      requestId: portalReqId
    };
    this.bissLogin()
    return this.http.post(AppConfig.endpoints.eventLog, requestBody, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getCertificateInfo'))
      );
  }

  private bissSign(result: SignedContent[]): Observable<BissSignedContent[]> {
    const url = 'https://localhost:' + this.usedPort + '/sign';

    const contentsArr: string[] = [];
    const signedContentsArr: string[] = [];
    const signedContentsCertArr: string[] = [];

    result.forEach(c => {
      contentsArr.push(c.content);
      signedContentsArr.push(c.signedContent);
      signedContentsCertArr.push(c.signedContentCert);
    });

    const objToSignWithBiss = {
      version: '2.16',
      contents: contentsArr,
      signedContents: signedContentsArr,
      signedContentsCert: signedContentsCertArr,
      contentType: 'digest',
      hashAlgorithm: 'SHA256',
      signatureType: 'signature',
      signerCertificateB64: this.usedCert,
      confirmText: null
    };

    return this.http.post(url, objToSignWithBiss).pipe(
      switchMap((bissResponse: IBissLoginResponse) => {
        if (bissResponse.status === 'ok') {
          const bissSignedContents: BissSignedContent[] = [];
          for (let i = 0; i < bissResponse.signatures.length; i++) {
            bissSignedContents.push(
              new BissSignedContent(
                contentsArr[i],
                bissResponse.signatures[i],
                this.usedCert,
                result[i].originalContent,
                result[i].digestTime,
                result[i].filename,
                result[i].type));
          }
          this.logEvent(EventType.BISS_SIGN_DOC, false).subscribe(res => { });
          return of(bissSignedContents); // Use 'of' instead of 'Observable.of'
        } else {
          this.logEvent(EventType.BISS_SIGN_DOC, true).subscribe(res => { });
          throw new BissError('');
        }
      }),
      catchError(e => this.handleBissError(e))
    );
  }

  private checkBiss(): Observable<any> {
    const ports = [53952, 53953, 53954];
    const checks: Observable<any>[] = [];
    let activePort: number | undefined;

    for (const port of ports) {
      const obs = this.http.get<{ version: number }>(`https://localhost:${port}/version`).pipe(
        catchError(() => of(null))
      );

      checks.push(obs);

      obs.subscribe((res) => {
        if (res && res.version >= 1) {
          activePort = port;
        }
      });
    }

    return forkJoin(checks).pipe(
      switchMap(() => {
        if (activePort !== undefined) {
          return of(activePort);
        } else {
          return of(null);
        }
      })
    );
  }

  private downloadBiss() {
    this.bissDialogRef.componentInstance.changeBissDownloadText();
    this.bissDialogRef = this.dialog.open(BissDialogComponent);
    let url: string;
    url = this.downloadInfo.bissDownloadLink
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    this.logEvent(EventType.DOWNLOAD_BISS, false).subscribe(res => {
    });
  }
  handleBissError(e: BissError | any): Observable<any> {
    const errMessage = e instanceof BissError ? e.message : this.handleHttpError(e);
    this.dialog.closeAll();
    return empty();
  }
  handleHttpError(e: any) {
    if (e.status != 200) {
      this.info.error(e.error.reasonText);
    }
    if (e.error.reasonText) {
      return e.error.reasonText;
    }
  }
  private validate(bissResult: BissSignedContent[]): Observable<any | any> {
    const url = AppConfig.endpoints.bissLogin;

    const validateLoginRequest = {
      signature: bissResult[0].signature,
      content: bissResult[0].content,
      certificate: bissResult[0].certificate
    };
    return this.http.post<any>(url, validateLoginRequest,
      this.headerHelper.options(observeTypes.BODY)).pipe(
        catchError(this.handleError('bissError'))
      );
  }

  signBiss(payload: any): Observable<any> {
    const ports = [53952, 53953, 53954];
    for (const port of ports) {

      return this.http.post(`https://localhost:${port}/sign`, payload).pipe(
        catchError(error => {
          console.error('Signing error:', error);
          return of(null);
        })
      );
    }
  }
  postBiss(payload: any): Observable<any> {
    const url = AppConfig.endpoints.bissLogin;
    return this.http.post(`${url}`, payload, {
      headers: new HttpHeaders({
        'Cookie': 'JSESSIONID=dummyValue'
      }),
      withCredentials: true
    }).pipe(
      catchError(error => {
        if (error?.status === 404 || error?.status == null) {
          console.log(error)
          const key = error?.error?.code === 'ACTIVE_CERTIFICATES_NOT_FOUND'
            ? 'bissLogin.activeCertificatesNotFound'
            : 'bissLogin.loginError';
          this.translateService.get(key).subscribe((message: string) => {
            this.info.error(message);
          });
          return of(null);
        }
        return of(null);
      })
    );
  }

}
